import { InfoRow } from '@/components/InfoRow';
import { Section, SectionGroup } from '@/components/Section';
import { Accordion } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Dot } from '@/components/ui/dot';
import { formatUtc } from '@/lib/format';
/**
 * Flight Ops tab (`ldt-flight`, spec §14):
 *   1. Flight Details (always-visible)
 *   2. GCS & Control accordion
 *   3. Control Input accordion (Sticks vs Dock)
 *   4. Position Prediction accordion
 *   5. Return to Home accordion
 *   6. Mission Progress accordion
 *   7. Video Streams accordion
 */
import type { DroneLive } from '@/lib/store/drones';
import { useWaypointProgressStore } from '@/lib/store/waypointProgress';
import {
  type ReachedInfo,
  buildWaypointProgress,
  estimateFinalWaypoint,
  formatCountdown,
} from '@/lib/waypoints/progress';

// Stable reference for the "no recorded pass times yet" case — a fresh `{}`
// on every render would fail Zustand's useSyncExternalStore reference-equality
// check and force a render loop (caught by FlightOps.waypointProgress.test.tsx).
const EMPTY_PASSED_AT: Record<number, string> = {};
const EMPTY_REACHED: Record<number, ReachedInfo> = {};

export function FlightOpsTab({ drone }: { drone: DroneLive }) {
  const oi = drone.oi;
  const t = drone.telemetry;
  const c2 = drone.c2;
  // ENG-86 live waypoint progress — only present once a waypoint_pred alert
  // has arrived for this drone; the route itself (oi.waypoints) is real
  // whenever the OI envelope carries one. Falls back to the (currently
  // always-empty, see oi.ts adapters) oi.mission fields below when there's
  // no route at all, so mock/demo mode keeps its existing "—" behaviour.
  const pred = useWaypointProgressStore((s) => s.latestPredByDrone[t.drone_id] ?? null);
  const passedAt = useWaypointProgressStore(
    (s) => s.passedAtByDrone[t.drone_id] ?? EMPTY_PASSED_AT,
  );
  // ENG-40 ask #1: only trust a "missed" waypoint once this drone's BE
  // session has actually sent a structured index on waypoint_approached —
  // see progress.ts's module header. False today; flips on its own once
  // BE ships the ask, no FE change needed then.
  const missedDetectionEnabled = useWaypointProgressStore(
    (s) => s.structuredApproachedByDrone[t.drone_id] ?? false,
  );
  // BE's per-waypoint verdicts for the current lap (ENG-211) and where
  // tracking began. Module-level EMPTY_* keep the selectors referentially
  // stable so a missing entry can't force a render loop (see the note above).
  const reached = useWaypointProgressStore((s) => s.reachedByDrone[t.drone_id] ?? EMPTY_REACHED);
  const untrackedBelow = useWaypointProgressStore(
    (s) => s.untrackedBelowByDrone[t.drone_id] ?? null,
  );
  const verdicts = useWaypointProgressStore((s) => s.verdictsByDrone[t.drone_id] ?? false);
  const waypointProgress = oi?.waypoints
    ? buildWaypointProgress(oi.waypoints, pred, passedAt, missedDetectionEnabled, {
        reached,
        untrackedBelow,
        verdicts,
      })
    : null;
  const nextWaypoint = waypointProgress?.find((p) => p.status === 'next') ?? null;
  const doneCount = waypointProgress?.filter((p) => p.status === 'done').length ?? 0;
  const missedCount = waypointProgress?.filter((p) => p.status === 'missed').length ?? 0;
  const untrackedCount = waypointProgress?.filter((p) => p.status === 'untracked').length ?? 0;
  // Client-side estimate — BE only predicts the immediate target. Null
  // while the final waypoint is itself the target (the Next line covers it).
  const finalEstimate =
    oi?.waypoints && pred ? estimateFinalWaypoint(oi.waypoints, pred, Date.now()) : null;

  const gcsMeta =
    c2?.gcs_platform_name && c2.gcs_version
      ? `${c2.gcs_platform_name} v${c2.gcs_version}`
      : (c2?.gcs_platform_name ?? '—');
  // Control-input mode label: dock deployment shows DOCK, sticks shows
  // TRADITIONAL (matches mockup wording). Anything else → null badge.
  const inputMode =
    c2?.deployment === 'dock' ? 'DOCK' : c2?.deployment === 'sticks' ? 'TRADITIONAL' : null;
  const controllerLabel = c2?.deployment === 'dock' ? c2.dock_id : (c2?.sticks_type ?? null);
  const inputConnected = controllerLabel != null;

  return (
    <SectionGroup>
      <Section title="Flight Details">
        <div className="rounded-md border border-(--color-border) bg-(--color-surface) px-3 py-2">
          <InfoRow label="Flight Name" value={`Survey — ${t.callsign}`} />
          <InfoRow
            label="Pilot in Command"
            value={oi?.pilot_name ?? '—'}
            valueClassName="font-bold"
          />
        </div>
      </Section>

      <Accordion title="GCS & Control" meta={gcsMeta}>
        <InfoRow
          label="GCS Platform"
          value={c2?.gcs_platform_name ?? '—'}
          valueClassName="font-bold"
        />
        <InfoRow label="Version" value={c2?.gcs_version ?? '—'} mono />
        <InfoRow label="Autopilot" value={c2?.autopilot ?? '—'} mono />
        <InfoRow label="Flight Controller" value={c2?.flight_controller ?? '—'} mono />
      </Accordion>

      <Accordion
        title="Control Input"
        meta={
          <span className="flex items-center gap-1.5">
            {inputMode && <Badge variant="info">{inputMode}</Badge>}
            <Dot variant={inputConnected ? 'ok' : 'muted'} size="xs" />
            <span
              className={
                inputConnected ? 'text-(--color-green) font-bold' : 'text-(--color-text3) font-bold'
              }
            >
              {inputConnected ? 'Connected' : '—'}
            </span>
          </span>
        }
      >
        <InfoRow label="Controller" value={controllerLabel ?? '—'} mono />
        <InfoRow
          label="Status"
          value={inputConnected ? 'Connected' : '—'}
          valueClassName={inputConnected ? 'text-(--color-green)' : 'text-(--color-text3)'}
        />
      </Accordion>

      <Accordion
        title="Position Prediction"
        meta={
          <span className="flex items-center gap-1.5">
            {t.flight_mode && <Badge variant="info">{t.flight_mode}</Badge>}
            {t.pred1min?.inside != null && (
              <Badge variant={t.pred1min.inside ? 'ok' : 'crit'}>
                {t.pred1min.inside ? 'Inside Volume' : 'Outside Volume'}
              </Badge>
            )}
          </span>
        }
      >
        <div className="space-y-1">
          <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            Algorithm Inputs
          </div>
          <InfoRow label="Mode" value={t.flight_mode ?? '—'} />
          <InfoRow
            label="Ground Speed"
            value={t.groundspeed != null ? `${t.groundspeed.toFixed(1)} m/s` : '—'}
            mono
          />
          <InfoRow label="Heading" value={`${Math.round(t.heading)}°`} mono />
          <InfoRow
            label="Vert. Speed"
            value={t.climbRate != null ? `${t.climbRate.toFixed(1)} m/s` : '—'}
            mono
          />
          <InfoRow
            label="Acceleration"
            value={t.acceleration != null ? `${t.acceleration.toFixed(1)} m/s²` : '—'}
            mono
          />
        </div>
        <div className="h-px bg-(--color-border-lite) my-2" />
        <div className="space-y-1">
          <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            Predicted Position (T+60s)
          </div>
          <InfoRow
            label="Coordinates"
            value={t.pred1min ? `${t.pred1min.lat.toFixed(3)}, ${t.pred1min.lng.toFixed(3)}` : '—'}
            mono
          />
          <InfoRow
            label="OI Volume"
            value={t.pred1min?.inside == null ? '—' : t.pred1min.inside ? 'Inside' : 'Outside'}
            mono
          />
        </div>
        <div className="h-px bg-(--color-border-lite) my-2" />
        <div className="space-y-1">
          <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            Assessment
          </div>
          <InfoRow label="OI Volume Updated" value={formatUtc(oi?.last_assessment_at)} mono />
          <InfoRow label="Last Assessment" value="just now" />
        </div>
      </Accordion>

      <Accordion
        title="Return to Home"
        meta={
          oi?.rth ? (
            <span className="flex items-center gap-1.5">
              <Dot variant={oi.rth.home_locked ? 'ok' : 'muted'} size="xs" />
              <span
                className={
                  oi.rth.home_locked
                    ? 'text-(--color-green) font-bold'
                    : 'text-(--color-text3) font-bold'
                }
              >
                {oi.rth.home_locked ? 'Home Locked' : 'Home Not Locked'}
              </span>
            </span>
          ) : (
            <span className="text-(--color-text3) font-bold">—</span>
          )
        }
      >
        {oi?.rth ? (
          <>
            {oi.rth.trigger && (
              <div className="text-[0.692em] font-mono text-(--color-text3) mb-2">
                Trigger: {oi.rth.trigger}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <RthTile label="RTH Altitude" value={`${oi.rth.altitude_ft} ft`} />
              <RthTile label="Return Speed" value={`${oi.rth.return_speed_mps} m/s`} />
              <RthTile
                label="Safety Alt"
                value={oi.rth.safety_alt_ft != null ? `${oi.rth.safety_alt_ft} ft` : '—'}
                tone="amber"
              />
              <RthTile label="Est. Time" value={oi.rth.est_time ?? '—'} />
              <RthTile
                label="Est. Distance"
                value={oi.rth.est_distance_km != null ? `${oi.rth.est_distance_km} km` : '—'}
              />
              <RthTile label="Landing Mode" value={oi.rth.landing_mode ?? '—'} tone="green" />
            </div>
            {oi.rth.home_lat != null && oi.rth.home_lng != null && (
              <div className="text-[0.615em] font-mono text-(--color-text3) border-t border-(--color-border-lite) pt-1.5 mt-2">
                Home: {oi.rth.home_lat}, {oi.rth.home_lng}
              </div>
            )}
          </>
        ) : (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4">
            No RTH configuration.
          </div>
        )}
      </Accordion>

      <Accordion
        title="Mission Progress"
        meta={
          waypointProgress ? (
            <span className="flex items-center gap-1.5">
              <span className="text-(--color-green) font-bold font-mono">
                {doneCount} / {waypointProgress.length}
              </span>
              <span className="text-(--color-text3)">waypoints</span>
              {missedCount > 0 && (
                <span className="text-(--color-red) font-bold font-mono">
                  · {missedCount} missed
                </span>
              )}
              {untrackedCount > 0 && (
                <span className="text-(--color-text3) font-mono">
                  · {untrackedCount} not tracked
                </span>
              )}
            </span>
          ) : oi?.mission ? (
            <span className="flex items-center gap-1.5">
              <span className="text-(--color-green) font-bold font-mono">
                {oi.mission.waypoints_done} / {oi.mission.waypoints_total}
              </span>
              <span className="text-(--color-text3)">waypoints</span>
            </span>
          ) : (
            <span className="text-(--color-text3) font-bold">—</span>
          )
        }
      >
        {waypointProgress ? (
          (() => {
            const pct =
              waypointProgress.length > 0
                ? Math.round((doneCount / waypointProgress.length) * 100)
                : 0;
            const isFinal =
              nextWaypoint != null && nextWaypoint.index === waypointProgress.length - 1;
            const etaLabel = nextWaypoint ? (formatCountdown(nextWaypoint.etaSeconds) ?? '—') : '—';
            return (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[0.615em] uppercase tracking-wider text-(--color-text3) mb-0.5">
                    Next Waypoint
                  </div>
                  <div className="text-[0.923em] font-bold font-mono">
                    {nextWaypoint ? `#${nextWaypoint.index + 1}${isFinal ? ' (Final)' : ''}` : '—'}
                  </div>
                  <div className="text-[0.692em] font-mono text-(--color-text3)">
                    {nextWaypoint?.distanceMeters != null
                      ? `${(nextWaypoint.distanceMeters / 1000).toFixed(2)} km ahead · ${etaLabel}`
                      : etaLabel}
                  </div>
                </div>
                <div>
                  <div className="text-[0.615em] uppercase tracking-wider text-(--color-text3) mb-0.5">
                    Progress
                  </div>
                  <div className="h-1.5 bg-(--color-border) rounded mt-1.5 overflow-hidden">
                    <div className="h-full bg-(--color-green)" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[0.615em] font-mono text-(--color-text3) mt-1">
                    {pct}% complete
                  </div>
                </div>
                {finalEstimate && (
                  <div>
                    <div className="text-[0.615em] uppercase tracking-wider text-(--color-text3) mb-0.5">
                      Final Waypoint
                    </div>
                    <div className="text-[0.923em] font-bold font-mono">
                      #{waypointProgress.length}
                    </div>
                    <div className="text-[0.692em] font-mono text-(--color-text3)">
                      {`${(finalEstimate.distanceMeters / 1000).toFixed(2)} km ahead · ${formatCountdown(finalEstimate.etaSeconds) ?? '—'}`}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : oi?.mission ? (
          (() => {
            const pct =
              oi.mission.waypoints_total > 0
                ? Math.round((oi.mission.waypoints_done / oi.mission.waypoints_total) * 100)
                : 0;
            return (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[0.615em] uppercase tracking-wider text-(--color-text3) mb-0.5">
                    Next Waypoint
                  </div>
                  <div className="text-[0.923em] font-bold font-mono">
                    {oi.mission.next_wp_id ?? '—'}
                  </div>
                  <div className="text-[0.692em] font-mono text-(--color-text3)">
                    {oi.mission.dist_to_next_km != null
                      ? `${oi.mission.dist_to_next_km} km ahead`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[0.615em] uppercase tracking-wider text-(--color-text3) mb-0.5">
                    Progress
                  </div>
                  <div className="h-1.5 bg-(--color-border) rounded mt-1.5 overflow-hidden">
                    <div className="h-full bg-(--color-green)" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[0.615em] font-mono text-(--color-text3) mt-1">
                    {pct}% complete
                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4">
            No mission in progress.
          </div>
        )}
      </Accordion>

      <Accordion
        title="Video Streams"
        meta={(() => {
          const streams = c2?.video_streams ?? [];
          const active = streams.filter((s) => s.active).length;
          return streams.length === 0 ? (
            <span className="text-(--color-text3) font-bold">—</span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span
                className={
                  active > 0
                    ? 'text-(--color-green) font-bold font-mono'
                    : 'text-(--color-text3) font-bold font-mono'
                }
              >
                {active}
              </span>
              <span className="text-(--color-text3)">active</span>
            </span>
          );
        })()}
      >
        {(c2?.video_streams ?? []).length === 0 ? (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4">
            No video streams.
          </div>
        ) : (
          <div className="space-y-2">
            {(c2?.video_streams ?? []).map((s) => (
              <div
                key={s.name}
                className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2"
              >
                <div className="flex items-center gap-1.5">
                  <Dot variant={s.active ? 'ok' : 'muted'} size="xs" />
                  <span className="text-[0.769em] font-bold">{s.name}</span>
                  <Badge variant={s.active ? 'ok' : 'muted'}>
                    {s.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="text-[0.692em] font-mono text-(--color-text3) mt-1">
                  {s.protocol}
                  {s.latency_ms != null && ` · ${s.latency_ms} ms`}
                  {s.url && ` · ${s.url}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </Accordion>
    </SectionGroup>
  );
}

function RthTile({
  label,
  value,
  tone,
}: { label: string; value: string; tone?: 'amber' | 'green' }) {
  return (
    <div className="rounded-md bg-(--color-bg) p-2 border border-(--color-border)">
      <div
        className={`text-[0.846em] font-bold font-mono ${tone === 'amber' ? 'text-(--color-amber)' : tone === 'green' ? 'text-(--color-green)' : ''}`}
      >
        {value}
      </div>
      <div className="text-[0.615em] text-(--color-text3) mt-0.5">{label}</div>
    </div>
  );
}
